import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import { ITipoServico } from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { TipoServicoDataService } from './tipo-servico.dataservice';

/**
 * Service HTTP para TipoServico
 */
@Injectable({
  providedIn: 'root',
})
export class TipoServicoService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);
  private dataService = inject(TipoServicoDataService);

  private get baseUrl(): string {
    return `${this.env.getApiUrl('bardo')}/tipoServico`;
  }

  async findAll(filtro?: unknown): Promise<ITipoServico[]> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const tiposServico = await firstValueFrom(
        this.http.post<ITipoServico[]>(`${this.baseUrl}/findAll`, filtro || {})
      );

      this.dataService.setTiposServico(tiposServico);
      return tiposServico;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar tipos de serviço';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async findById(id: string): Promise<ITipoServico> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const tipoServico = await firstValueFrom(
        this.http.get<ITipoServico>(`${this.baseUrl}/findById`, {
          params: { id: id.toString() },
        })
      );

      this.dataService.setTipoServicoAtual(tipoServico);
      return tipoServico;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar tipo de serviço';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async criar(tipoServico: Partial<ITipoServico>): Promise<string> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const id = await firstValueFrom(
        this.http.post<string>(`${this.baseUrl}/create`, tipoServico)
      );

      if (id) {
        this.dataService.addTipoServico({
          ...(tipoServico as ITipoServico),
          id,
        });
      }

      return id;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao criar tipo de serviço';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  /**
   * Criar múltiplos tipos de serviço
   */
  async criarMultiplo(
    tiposServico: Partial<ITipoServico>[]
  ): Promise<string[]> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const ids = await firstValueFrom(
        this.http.post<string[]>(`${this.baseUrl}/createMultiple`, tiposServico)
      );

      await this.findAll();
      return ids;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao criar tipos de serviço';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async atualizar(
    id: string,
    tipoServico: Partial<ITipoServico>
  ): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      await firstValueFrom(
        this.http.put<void>(`${this.baseUrl}/update`, {
          ...tipoServico,
          id,
        })
      );

      this.dataService.updateTipoServico(id, tipoServico);
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao atualizar tipo de serviço';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async deletar(id: string): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      await firstValueFrom(
        this.http.delete<void>(`${this.baseUrl}/delete`, {
          params: { id: id.toString() },
        })
      );

      this.dataService.removeTipoServico(id);
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao deletar tipo de serviço';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async ativar(id: string): Promise<void> {
    await this.atualizar(id, { status: true });
  }

  async desativar(id: string): Promise<void> {
    await this.atualizar(id, { status: false });
  }
}
